import React, { useState, useContext, useEffect } from 'react'
import { Modal, Form } from 'react-bootstrap'
import { ModelContext } from '@abroad/components'
import { useHistory, useParams } from 'react-router-dom'
import { Button } from '@abroad/components'
import { sowStatus } from '@abroad/components/src/helper/common'

/**
 * Add Session modal.
 *
 * Used from:
 * - Client profile Coaches & SOWs ("Add A Session") — select from clientSOWs
 * - Scheduling booked list ("Add log session") — pass prefilledPlan (+ optional scheduledEventId)
 */
const AddSessionModal = ({
  clientSOWs,
  userInfo,
  prefilledPlan = null,
  scheduledEventId = null,
  scheduledEvent = null,
  overrideClientId = null,
  modalTitle = 'Add A Session',
}) => {
  const { show, onHide: closeModal } = useContext(ModelContext)
  const history = useHistory()
  const { clientId: routeClientId } = useParams()
  const clientId = overrideClientId || routeClientId
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    planId: '',
    sessionType: '',
  })
  const [errors, setErrors] = useState({})

  // Normalize prefilled plan from scheduling list (BE: includeGrowthSession, sowType, planName)
  const resolvedPrefilledPlan = prefilledPlan
    ? {
        ...prefilledPlan,
        includeGrowthSession: !!prefilledPlan.includeGrowthSession,
        sowType:
          (prefilledPlan.sowType || '').toString().toUpperCase() === 'QUEST'
            ? 'QUEST'
            : 'REGULAR',
        planName: prefilledPlan.planName || '',
      }
    : null

  // Filter out B2B plans and get individual plans with valid status and sessions
  const individualPlans =
    !prefilledPlan &&
    (clientSOWs?.filter(
      (sow) =>
        sow?.type === 'individual' &&
        sow?.coachPlans?.some(
          (plan) =>
            plan.isCoachOfCurrentPlan === true &&
            [
              sowStatus.IN_PROGRESS,
              sowStatus.TERMINATED,
              sowStatus.PAYMENT_FAILED,
            ].includes(plan.sowStatus) &&
            (plan.remainingSession > 0 || plan.billingIndefinite === true),
        ),
    ) ||
      [])

  // Flatten all valid coach plans from individual SOWs (or locked prefilled plan)
  const allPlans = resolvedPrefilledPlan
    ? [resolvedPrefilledPlan]
    : (individualPlans || []).reduce((acc, sow) => {
        if (sow?.coachPlans) {
          const validPlans = sow.coachPlans.filter(
            (plan) =>
              plan.isCoachOfCurrentPlan === true &&
              [
                sowStatus.IN_PROGRESS,
                sowStatus.TERMINATED,
                sowStatus.PAYMENT_FAILED,
              ].includes(plan.sowStatus) &&
              (plan.remainingSession > 0 || plan.billingIndefinite === true),
          )

          if (validPlans.length > 0) {
            acc.push(...validPlans)
          }
        }
        return acc
      }, [])

  // Auto-select if only one plan (always true for prefilled / Add log session)
  const shouldAutoSelect = !!resolvedPrefilledPlan || allPlans.length === 1
  const defaultPlanId = shouldAutoSelect ? allPlans[0]?._id || '' : ''

  const handleModalClose = () => {
    setTimeout(() => {
      setFormData({
        planId: defaultPlanId,
        sessionType: '',
      })
      setErrors({})
    }, 500)
    closeModal()
  }

  // Initialize form data with default plan if auto-select is enabled
  useEffect(() => {
    if (
      shouldAutoSelect &&
      defaultPlanId &&
      formData.planId !== defaultPlanId
    ) {
      setFormData((prev) => ({
        ...prev,
        planId: defaultPlanId,
      }))
    }
  }, [shouldAutoSelect, defaultPlanId, formData.planId])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }

    // Reset session type when plan changes
    if (name === 'planId') {
      setFormData((prevData) => ({
        ...prevData,
        sessionType: '',
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    setIsLoading(true)
    try {
      const selectedPlan = allPlans.find((plan) => plan._id === formData.planId)

      const navigationState = {
        selectedPlan: {
          _id: selectedPlan._id,
          planName: selectedPlan.planName,
          sessionDuration: selectedPlan.sessionDuration,
        },
        ...(scheduledEventId
          ? {
              scheduledEventId,
              scheduleEventId: scheduledEventId,
              fromScheduledBooking: true,
              scheduledEvent: scheduledEvent
                ? {
                    _id: scheduledEventId,
                    title: scheduledEvent.title || '',
                    startDateTime: scheduledEvent.startDateTime || null,
                  }
                : {
                    _id: scheduledEventId,
                    title: '',
                    startDateTime: null,
                  },
            }
          : {}),
      }

      if (formData.sessionType === 'growth-plan') {
        // Redirect to growth plan creation with plan data in state
        history.push(
          `/admin/users/${clientId}/growth-plan/${selectedPlan._id}/create`,
          navigationState,
        )
      } else if (formData.sessionType === 'regular') {
        // Redirect to regular session creation with plan data in state
        history.push(`/coach/clients/${clientId}/session`, navigationState)
      }

      handleModalClose()
    } catch (error) {
      setErrors({ submit: 'Failed to process request. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  const selectedPlan = allPlans.find((plan) => plan._id === formData.planId)
  const selectedPlanId = selectedPlan ? selectedPlan._id : ''
  const isSessionTypeDisabled = !formData.planId

  // Derive which session type options to show based on growth flag
  const includeGrowth = !!selectedPlan?.includeGrowthSession
  const isQuestSow = selectedPlan?.sowType === 'QUEST'
  const regularSessionLabel = isQuestSow
    ? 'Quest Integration Session'
    : 'Regular'
  const growthSessionLabel = isQuestSow ? 'Quest Preparation Session' : 'Growth'

  // If includeGrowthSession === true -> show both Regular and Growth
  // If includeGrowthSession === false -> show only Regular
  const showBoth = includeGrowth
  const showRegularOnly = !includeGrowth

  // If there's only a single valid option (Regular only), auto-select it
  useEffect(() => {
    if (!selectedPlanId) return

    const singleOption = showRegularOnly ? 'regular' : ''
    if (singleOption && formData.sessionType !== singleOption) {
      setFormData((prev) => ({ ...prev, sessionType: singleOption }))
      if (errors.sessionType)
        setErrors((prev) => ({ ...prev, sessionType: '' }))
    }
  }, [
    selectedPlanId,
    showRegularOnly,
    formData.sessionType,
    errors.sessionType,
  ])

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      centered
      backdrop='static'
      keyboard={false}
      className='payment-modal'>
      <style>
        {
          '\n        select:disabled, .form-control:disabled, .form-control[readonly] {\n          background-color: #999 !important;\n          cursor: not-allowed !important;\n          opacity: 0.6 !important;\n        }\n        .add-session-select {\n          -webkit-appearance: menulist;\n          -moz-appearance: menulist;\n          appearance: menulist;\n          min-height: 40px;\n        }\n        .add-session-select option {\n          white-space: normal;\n          padding: 8px 12px;\n          width: 100%;\n          display: block;\n          overflow: visible;\n          text-overflow: unset;\n        }\n        .form-control {\n          text-overflow: ellipsis;\n        }\n      '
        }
      </style>
      <Modal.Header className='payment-modal-header d-flex justify-content-center'>
        <h5 className='payment-modal-title mb-0'>{modalTitle}</h5>
      </Modal.Header>
      <Modal.Body className='payment-modal-body p-3'>
        <Form onSubmit={handleSubmit}>
          <Form.Group className='mb-0'>
            <Form.Label className='s8 text-uppercase'>PLAN NAME</Form.Label>

            <Form.Control
              as='select'
              name='planId'
              value={formData.planId}
              onChange={handleChange}
              isInvalid={!!errors.planId}
              required
              disabled={shouldAutoSelect}
              className='add-session-select input-typo text-dark-900 text-capitalize w-100'
              style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {!shouldAutoSelect && (
                <option value='' disabled>
                  Select a Plan Name{' '}
                </option>
              )}
              {allPlans.map((plan) => (
                <option
                  key={plan._id}
                  value={plan._id}
                  title={plan.planName}
                  style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                  {plan.planName || 'Selected Plan'}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type='invalid' className='text-danger s9'>
              {errors.planId}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className='mt-4'>
            <Form.Label className='s8 text-uppercase'>SESSION TYPE</Form.Label>
            <Form.Control
              as='select'
              name='sessionType'
              value={formData.sessionType}
              onChange={handleChange}
              isInvalid={!!errors.sessionType}
              disabled={isSessionTypeDisabled || isLoading}
              className='input-typo text-dark-900 w-100'
              required>
              <option value='' disabled>
                Select Session Type
              </option>
              {showRegularOnly && (
                <option value='regular'>{regularSessionLabel}</option>
              )}
              {showBoth && (
                <>
                  <option value='regular'>{regularSessionLabel}</option>
                  <option value='growth-plan'>{growthSessionLabel}</option>
                </>
              )}
            </Form.Control>
            <Form.Control.Feedback type='invalid' className='text-danger s9'>
              {errors.sessionType}
            </Form.Control.Feedback>
          </Form.Group>

          {errors.submit && (
            <div className='alert alert-danger s9 mb-3'>{errors.submit}</div>
          )}

          <div className='d-flex justify-content-end mt-4'>
            <Button
              variant='secondary'
              onClick={handleModalClose}
              className='px-3 mr-2 secondary-popup-button'
              size='sm'
              disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type='submit'
              variant='saffron'
              size='sm'
              disabled={isLoading || !formData.planId || !formData.sessionType}
              isLoading={isLoading}
              className='text-uppercase border-radius-normal text-white hover:text-white save-btn-fixed-width'>
              Add Session
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  )
}

export default AddSessionModal
